import { html, nothing } from 'lit';

import { ifDefined } from 'lit/directives/if-defined.js';
import { ref } from 'lit/directives/ref.js';

import { requiredValidator } from '../utilities/validators';
import { live } from 'lit/directives/live.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { FloatField } from './float_field';

export default class FloatCheckbox extends FloatField {
  static get properties() {
    return {
      ...super.properties,
      checked: { 
        type: Boolean, 
        reflect: true, 
        converter: {
          fromAttribute: (value) => {
            if (value === null) return false;
            if (value === '') return true;
            return value !== 'false';
          },
          toAttribute: (value) => {
            return value ? '' : null;
          }
        }
      },
      additionalInfo: { type: String, attribute: 'additional-info' },
    };
  }

  constructor() {
    super();
    this.tabIndex = 0;
    this.validateOnly = false;
    this.type = 'checkbox';
    this._checked = false;

    this.validators = [
      {
        ...requiredValidator,
        isValid: () => requiredValidator.isValid(this),
      },
    ];
  }

  get checked() {
    return this._checked;
  }

  set checked(value) {
    const oldValue = this._checked;
    this._checked = value === true || value === 'true' || (value === '' && this.hasAttribute('checked'));
    this.requestUpdate('checked', oldValue);
  }

  get value() {
    return this.input?.checked || false;
  }

  set value(value) {
    this._value = value;
    this.setFormValue(value);
    this.updateValidity();
    this.setInvalidState();
  }

  get valueString() {
    const attrValue = this.getAttribute('value');
    return attrValue !== null ? attrValue : 'true';
  }

  connectedCallback() {
    super.connectedCallback();
    this.setFormValue(this._value);
    this.updateValidity();
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (changedProperties.has('checked') && this.input) {
      this.input.checked = this.checked;
    }
  }

  firstUpdated() {
    if (this.isConnected) {
      setTimeout(() => {
        if (this.input && this.input?.checked) {
          if (!this.input?.checked) {
            return
          }

          this.hasInteracted = true;
          this.value = this.input?.checked;
        }
      }, 100);
    }
  }

  handleChange() {
    super.handleChange();
    this.checked = this.input.checked;
  }

  renderAdditionalInfo() {
    return this.additionalInfo
      ? html`<p class="float_checkbox_additional_info">
          ${unsafeHTML(this.additionalInfo)}
        </p>`
      : nothing;
  }

  renderInput() {
    return html`
      <div class="float_checkbox_wrapper">
        <input
          ${ref((el) => (this.input = el))}
          id=${this.id}
          name=${this.name}
          type="checkbox"
          value=${ifDefined(this.valueString)}
          pattern=${ifDefined(this.pattern)}
          placeholder=${ifDefined(this.placeholder)}
          ?checked=${this.checked}
          ?readonly=${this.readOnly}
          ?disabled=${this.disabled}
          ?required=${this.required}
          ?autofocus=${this.autofocus}
          tabindex=${this.tabIndex}
          autocomplete=${ifDefined(this.autocomplete)}
          inputmode=${ifDefined(this.inputmode)}
          min=${ifDefined(this.validateOnly ? undefined : this.min)}
          max=${ifDefined(this.validateOnly ? undefined : this.max)}
          minlength=${ifDefined(this.minLength)}
          maxlength=${ifDefined(this.validateOnly ? undefined : this.maxLength)}
          step=${ifDefined(this.step)}
          aria-invalid=${this.invalid ? 'true' : 'false'}
          @change=${this.handleChange}
          @input=${this.handleInput}
          @focus=${this.handleFocus}
          @blur=${this.handleBlur}
        />
        ${this.renderAdditionalInfo()}
      </div>
    `;
  }
}
window.customElements.get('float-checkbox') ||
  window.customElements.define('float-checkbox', FloatCheckbox);
